import type { ZoomBookingMember } from '@nrx/booking-ui';
import { Alert, Box, Button, Card, Flex, Heading, Spinner, Text } from '@nrx/ui';
import { type JSX, useState } from 'react';

import type { ConsultType } from '../exchange/exchange-types.js';
import { InitialConsultBlock } from '../ffs/initial-consult-block.js';
import type { OneToOneSubsections } from '../ffs/one-to-one-subsections.js';

import { AdminFeeCardStep } from './admin-fee-card-step.js';
import { ConsultBookingFrame } from './consult-booking-frame.js';
import { StaffCardRequiredNotice } from './staff-card-required-notice.js';
import type { ConsultTaskAssignee } from './task-assignee.js';
import type { OneToOneConsultantsState } from './use-one-to-one-consultants.js';

type OneToOneTabProps = {
  consultType: ConsultType | undefined;
  /** Which blocks and headings this consult type + stage may show. */
  subsections: OneToOneSubsections;
  taskAssignee: ConsultTaskAssignee | null;
  consultants: OneToOneConsultantsState;
  member: ZoomBookingMember;
  /**
   * Task 10: when true, renders `AdminFeeCardStep` INSTEAD of every booking frame below. The
   * Observation this records, not the browser, is what un-gates the member — see
   * `AdminFeeCardStep`'s own docblock.
   */
  adminFeeCardRequired: boolean;
  /**
   * A care-betty staff member is booking for the member. Staff never enter a member's card, so a
   * required card shows {@link StaffCardRequiredNotice} instead of `AdminFeeCardStep` — still with no
   * booking frame, so the gate holds.
   */
  staffLink: boolean;
  /**
   * Session JWT for `AdminFeeCardStep`'s two payment calls. Always a real token by the time this
   * component renders `AdminFeeCardStep` — the page holds it back until the exchange resolves —
   * so the `?? ''` at the call site below is a type-satisfier, never a live fallback.
   */
  jwt: string | null;
  /** Re-runs the magic-link exchange after a card is authorized, to re-check the gate. */
  onAuthorized: () => void;
};

/**
 * 1:1 tab body: the FFS initial block, the section heading, the assignee CTA, and the program /
 * named-consultant CTAs.
 *
 * @remarks Order matters — the `taskAssignee` frame renders ABOVE the program CTA (tests 6 and 28).
 *
 * The consultant name is `Card title` with `headingLevel={4}`, so the heading comes from the
 * primitive rather than a hand-rolled `<h4>` (W1/W5) — `one-to-one-tab.spec.tsx` reaches it as
 * `getByRole('heading', { name: 'Mika D' })`, which the `card__title` element satisfies.
 *
 * The two async states were silent `<div>`s. They are now `Spinner` (`role="status"`) and
 * `Alert tone="danger"` (`role="alert"`), so a screen reader hears the consultant list resolve
 * (W4). The visible copy is unchanged: `Loading…` keeps its U+2026 ellipsis, distinct from the
 * exchange state's `Loading...`.
 */
export function OneToOneTab({
  consultType,
  subsections,
  taskAssignee,
  consultants,
  member,
  adminFeeCardRequired,
  staffLink,
  jwt,
  onAuthorized,
}: OneToOneTabProps): JSX.Element {
  // A care manager's deliberate, recorded choice to book without the member's card. Never saved:
  // the next booking asks again.
  const [cardWaived, setCardWaived] = useState(false);

  // Gates the WHOLE tab, ahead of every subsection flag below: a member who owes an
  // administrative fee sees no booking frame at all until the card step's confirm POST records
  // the Observation that lifts this.
  if (adminFeeCardRequired && staffLink && !cardWaived) {
    return <StaffCardRequiredNotice jwt={jwt ?? ''} onWaived={() => setCardWaived(true)} />;
  }
  if (adminFeeCardRequired && !staffLink) {
    return (
      <Box className="consult-admin-fee-step">
        <AdminFeeCardStep jwt={jwt ?? ''} onAuthorized={onAuthorized} />
      </Box>
    );
  }

  return (
    <OneToOneBooking
      consultType={consultType}
      subsections={subsections}
      taskAssignee={taskAssignee}
      consultants={consultants}
      member={member}
    />
  );
}

/**
 * The booking body behind the gate. A component of its own so its hooks run on every render of it:
 * the gate above returns early, and hooks after an early return change count between renders.
 */
function OneToOneBooking({
  consultType,
  subsections,
  taskAssignee,
  consultants,
  member,
}: Pick<
  OneToOneTabProps,
  'consultType' | 'subsections' | 'taskAssignee' | 'consultants' | 'member'
>): JSX.Element {
  // Every frame on this tab books a 1:1, even for a `flexible` link whose other tab is group.
  const bookedConsultType = consultType ?? '1:1';

  const {
    consultants: namedConsultants,
    initialConsultScheduleUrl,
    followUpConsultScheduleUrl,
    consultantsStatus,
    consultantsError,
  } = consultants;

  const sectionHeading = subsections.showGenericOneToOneScheduleHeading
    ? 'Schedule a consult'
    : subsections.showFollowUpHeading
      ? 'Follow-up Consult'
      : null;

  // Every branch below, named. This exists so the LAST one can be "nothing else rendered" — see
  // `showTerminalEmptyState`. Naming them rather than re-deriving the conditions inline is what
  // keeps the terminal branch honest when a branch above it changes.
  const showAssigneeFrame =
    subsections.showFollowUpBlock &&
    taskAssignee != null &&
    (consultType === '1:1' || consultType === 'flexible' || consultType === 'ffs');
  const showLoading = consultantsStatus === 'loading';
  const showError = consultantsStatus === 'error' && Boolean(consultantsError);
  const showProgramFrame =
    subsections.showFollowUpBlock &&
    consultantsStatus === 'loaded' &&
    Boolean(followUpConsultScheduleUrl);
  /**
   * WHICH calendar is on screen, kept separate from WHETHER each is available.
   *
   * `showAssigneeFrame` / `showProgramFrame` stay pure availability, because `showTerminalEmptyState`
   * below is computed from them: it exists because a real config combination once rendered literally
   * nothing — no frame, no message, no contact details — and folding selection into those flags
   * would make "not chosen" indistinguishable from "not available" to that guard.
   */
  const bothCalendarsAvailable = showAssigneeFrame && showProgramFrame;
  const [pickedTeamCalendar, setPickedTeamCalendar] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  // With one calendar there is no choice to offer, so the switch never appears and the frame that
  // is available is the frame that renders — byte-identical to the previous behaviour.
  const showingTeamCalendar = bothCalendarsAvailable ? pickedTeamCalendar : showProgramFrame;
  const renderAssigneeFrame = showAssigneeFrame && !showingTeamCalendar;
  const renderProgramFrame = showProgramFrame && showingTeamCalendar;
  // Retired once Zoom confirms: an inline "book with someone else" beside "Your consult is booked!"
  // reads like the booking did not take.
  const showCalendarSwitch = bothCalendarsAvailable && !bookingConfirmed;

  const showNoConsultantsState =
    subsections.showFollowUpBlock &&
    consultantsStatus === 'loaded' &&
    namedConsultants.length === 0 &&
    !followUpConsultScheduleUrl.trim();
  const showConsultantGrid =
    subsections.showFollowUpBlock && consultantsStatus === 'loaded' && namedConsultants.length > 0;

  /**
   * The ungated terminal branch (review finding 6).
   *
   * For `ffs` + `consultStage: 'initial'` + an empty `initialConsultScheduleUrl`,
   * `oneToOneSubsections` returns all four flags false — `hideFollowUpForStage` forces
   * `showFollowUpBlock` false independently of any URL — and EVERY branch above, the
   * "No 1:1 consultants available" state included, was gated on one of those flags. The member saw
   * literally nothing: no frame, no message, no link, no contact details, no recovery. That state is
   * reachable from the same operator-authored FFS_PROGRAM_SCHEDULE_CONFIG this PR already flags as
   * validating only non-emptiness.
   *
   * `idle` is deliberately excluded rather than treated as "nothing rendered": the consultants
   * request starts in a `useEffect`, so there is one painted frame of `idle` before `loading`, and
   * firing here would flash the empty state on the normal path.
   */
  const showTerminalEmptyState =
    (consultantsStatus === 'loaded' || consultantsStatus === 'error') &&
    !subsections.showInitialBlock &&
    sectionHeading == null &&
    !showAssigneeFrame &&
    !showLoading &&
    !showError &&
    !showProgramFrame &&
    !showNoConsultantsState &&
    !showConsultantGrid;

  return (
    <>
      {subsections.showInitialBlock ? (
        <InitialConsultBlock
          scheduleUrl={initialConsultScheduleUrl}
          member={member}
          consultType={bookedConsultType}
          showDivider={subsections.showFollowUpBlock}
        />
      ) : null}

      {sectionHeading != null ? (
        <Heading as="h3" size="6" className="consult-section-title">
          <span className="consult-accent">{sectionHeading}</span>
        </Heading>
      ) : null}

      {renderAssigneeFrame && taskAssignee != null && (
        <Box className="consult-booking-slot">
          <ConsultBookingFrame
            key={taskAssignee.scheduleUrl}
            bookingUrl={taskAssignee.scheduleUrl}
            member={member}
            consultType={bookedConsultType}
            title={`Schedule with ${taskAssignee.firstName}`}
            onBooked={() => setBookingConfirmed(true)}
          />
          {showCalendarSwitch ? (
            <Flex justify="center" pt="4">
              <Button tone="secondary" onClick={() => setPickedTeamCalendar(true)}>
                Book with another dietitian
              </Button>
            </Flex>
          ) : null}
        </Box>
      )}

      {showLoading && (
        <Flex justify="center" py="8">
          <Spinner message="Loading…" />
        </Flex>
      )}
      {showError && (
        <Alert tone="danger" className="consult-tab-alert">
          {consultantsError}
        </Alert>
      )}
      {renderProgramFrame && (
        <Box className="consult-booking-slot consult-booking-slot--wide">
          <ConsultBookingFrame
            key={followUpConsultScheduleUrl}
            bookingUrl={followUpConsultScheduleUrl}
            member={member}
            consultType={bookedConsultType}
            title="Schedule your consult"
            onBooked={() => setBookingConfirmed(true)}
          />
          {showCalendarSwitch && taskAssignee != null ? (
            <Flex justify="center" pt="4">
              <Button tone="secondary" onClick={() => setPickedTeamCalendar(false)}>
                Book with {taskAssignee.firstName} instead
              </Button>
            </Flex>
          ) : null}
        </Box>
      )}
      {(showNoConsultantsState || showTerminalEmptyState) && (
        <Box py="8">
          <Text as="p" align="center" color="muted">
            No 1:1 consultants available at this time.
          </Text>
        </Box>
      )}
      {showConsultantGrid && (
        <div className="consult-consultant-grid">
          {namedConsultants.map((consultant) => (
            <Card
              // Name AND url: two consultants sharing a first name is ordinary, and a bare `name`
              // key collides for them. `useOneToOneConsultants` guarantees both are non-empty.
              key={`${consultant.name}::${consultant.scheduleUrl}`}
              title={consultant.name}
              headingLevel={4}
              elevation="soft"
              className="consult-consultant-card"
            >
              <ConsultBookingFrame
                key={consultant.scheduleUrl}
                bookingUrl={consultant.scheduleUrl}
                member={member}
                consultType={bookedConsultType}
                title={`Schedule with ${consultant.name}`}
              />
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
